import { useEffect, useRef, useState } from "react";
import { ArrowRightIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { usePodcasts } from "@/hooks/usePodcasts";

interface FeedInputProps {
  onSubmit: (feedUrl: string) => Promise<void>;
  onSelectPodcast: (podcastSlug: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const applyCredentialsToUrl = (feedUrl: string, username: string, password: string) => {
  const trimmedUrl = feedUrl.trim();
  const trimmedUsername = username.trim();

  if (!trimmedUrl || (!trimmedUsername && !password)) {
    return trimmedUrl;
  }

  try {
    const url = new URL(trimmedUrl);
    if (trimmedUsername) {
      url.username = trimmedUsername;
    }
    if (password) {
      url.password = password;
    }
    return url.toString();
  } catch {
    return trimmedUrl;
  }
};

export default function FeedInput({ onSubmit, onSelectPodcast, isLoading, error }: FeedInputProps) {
  const [feedUrl, setFeedUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showCredentials, setShowCredentials] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedPodcast, setSelectedPodcast] = useState<{ id: string; name: string; emoji: string } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { podcasts, isLoading: podcastsLoading } = usePodcasts();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePodcastSelect = async (podcast: { id: string; name: string; emoji: string } | null) => {
    setSelectedPodcast(podcast);
    setIsDropdownOpen(false);
    if (podcast) {
      await onSelectPodcast(podcast.id);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedUrl.trim() || isLoading) return;
    const urlWithCredentials = showCredentials
      ? applyCredentialsToUrl(feedUrl.trim(), username, password)
      : feedUrl.trim();
    await onSubmit(urlWithCredentials);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
      <div className={cn(theme.cards.base, "p-8 md:p-12 max-w-2xl w-full")}>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">
          Try Episodes
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mb-8 text-lg">
          Chat with any podcast episode without subscribing.
          <br />
          Paste an RSS feed URL to get started.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex gap-3">
            <input
              type="url"
              value={feedUrl}
              onChange={(e) => setFeedUrl(e.target.value)}
              placeholder="https://example.com/feed.xml"
              className={cn(
                "flex-1 px-4 py-3 rounded-xl text-lg",
                theme.borders.neubrutalism,
                theme.shadows.neubrutalism,
                "bg-white dark:bg-gray-800",
                "text-gray-900 dark:text-white",
                "placeholder:text-gray-400 dark:placeholder:text-gray-500",
                "focus:outline-none focus:ring-2",
                theme.ring.primaryOrange
              )}
              disabled={isLoading}
              required
            />
            <button
              type="submit"
              disabled={isLoading || !feedUrl.trim()}
              className={cn(
                theme.buttons.primary,
                "px-5 py-3 rounded-xl",
                "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              )}
            >
              {isLoading ? (
                <span className="inline-block w-6 h-6 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
              ) : (
                <ArrowRightIcon className="w-6 h-6" />
              )}
            </button>
          </div>

          <button
            type="button"
            onClick={() => setShowCredentials((prev) => !prev)}
            className={cn(
              "inline-flex items-center gap-2 self-start rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wide transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-primary-orange/40 dark:focus:ring-primary-orange-dark/30",
              theme.colors.cardCream,
              theme.borders.neubrutalism,
              theme.shadows.neubrutalismSm
            )}
          >
            <span aria-hidden="true">{showCredentials ? "-" : "+"}</span>
            {showCredentials ? "Hide username & password" : "Add username & password"}
          </button>

          {showCredentials && (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="feed-username" className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  Username
                </label>
                <input
                  id="feed-username"
                  type="text"
                  autoComplete="username"
                  className={cn(
                    "w-full rounded-xl px-4 py-2 bg-white dark:bg-gray-800 text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2",
                    theme.borders.neubrutalism,
                    theme.shadows.neubrutalismSm,
                    theme.ring.primaryOrange
                  )}
                  placeholder="Optional"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="feed-password" className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  Password
                </label>
                <input
                  id="feed-password"
                  type="text"
                  autoComplete="current-password"
                  className={cn(
                    "w-full rounded-xl px-4 py-2 bg-white dark:bg-gray-800 text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2",
                    theme.borders.neubrutalism,
                    theme.shadows.neubrutalismSm,
                    theme.ring.primaryOrange
                  )}
                  placeholder="Optional"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
          )}

          {error && (
            <div className={cn(
              "p-4 rounded-xl",
              theme.borders.error,
              theme.colors.errorBg,
              theme.text.error
            )}>
              {error}
            </div>
          )}

          <p className="text-sm text-gray-500 dark:text-gray-400">
            Transcription costs ~2-4¢ per episode.
          </p>
        </form>

        {/* OR Separator */}
        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">OR</span>
          <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
        </div>

        {/* Podcast Picker Dropdown */}
        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            Choose from your Podcasts
          </label>
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              disabled={isLoading || podcastsLoading}
              className={cn(
                "w-full flex items-center justify-between px-4 py-3 rounded-xl text-left",
                theme.borders.neubrutalism,
                theme.shadows.neubrutalism,
                "bg-white dark:bg-gray-800",
                "text-gray-900 dark:text-white",
                "hover:-translate-y-0.5 transition-transform",
                "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              )}
            >
              {podcastsLoading ? (
                <span className="text-gray-400 dark:text-gray-500">Loading podcasts...</span>
              ) : selectedPodcast ? (
                <span className="flex items-center gap-2">
                  <span className="text-xl">{selectedPodcast.emoji}</span>
                  <span className="truncate">{selectedPodcast.name}</span>
                </span>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">None</span>
              )}
              <ChevronDownIcon
                className={cn("w-5 h-5 text-gray-500 transition-transform", isDropdownOpen && "rotate-180")}
              />
            </button>

            {isDropdownOpen && (
              <div
                className={cn(
                  "absolute top-full left-0 right-0 mt-2 max-h-60 overflow-y-auto z-50",
                  theme.borders.neubrutalism,
                  theme.shadows.neubrutalism,
                  "bg-white dark:bg-gray-800 rounded-xl"
                )}
              >
                {/* None option */}
                <button
                  type="button"
                  onClick={() => handlePodcastSelect(null)}
                  className={cn(
                    "w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors",
                    !selectedPodcast && "bg-gray-100 dark:bg-gray-700"
                  )}
                >
                  <span className="text-gray-500 dark:text-gray-400">None</span>
                </button>

                {/* Podcast options */}
                {podcasts.map((podcast) => (
                  <button
                    key={podcast.id}
                    type="button"
                    onClick={() => handlePodcastSelect(podcast)}
                    className={cn(
                      "w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2",
                      selectedPodcast?.id === podcast.id && "bg-gray-100 dark:bg-gray-700"
                    )}
                  >
                    <span className="text-xl">{podcast.emoji}</span>
                    <span className="truncate text-gray-900 dark:text-white">{podcast.name}</span>
                  </button>
                ))}

                {podcasts.length === 0 && !podcastsLoading && (
                  <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    No subscribed podcasts yet
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
          Sessions last 4 hours. Max 5 episodes per session.
        </p>
      </div>
    </div>
  );
}
